import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Home, Waves, ListVideo, BookOpen, BarChart3, CreditCard, Users, Settings, LogOut, Shield } from 'lucide-react';
import CreditBadge from '../Credits/CreditBadge';
import './NavigationMenu.css';

function NavigationMenu({ user, subscriptionStatus, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const allNavItems = [
    { 
      icon: Home, 
      labelKey: 'nav.dashboard', 
      path: '/dashboard',
      testId: 'nav-dashboard',
      showInHeader: true
    },
    { 
      icon: ListVideo, 
      labelKey: 'nav.programs', 
      path: '/programs',
      testId: 'nav-programs',
      showInHeader: true
    },
    { 
      icon: BookOpen, 
      labelKey: 'nav.journal', 
      path: '/notes',
      testId: 'nav-journal',
      showInHeader: true
    },
    { 
      icon: Users, 
      labelKey: 'nav.companion', 
      path: '/chat',
      testId: 'nav-chat',
      showInHeader: true
    },
    { 
      icon: Settings, 
      labelKey: 'nav.myAccount', 
      path: '/account',
      testId: 'nav-account',
      showInHeader: true
    },
    { 
      icon: Waves, 
      labelKey: 'nav.frequencies', 
      path: '/dashboard',
      testId: 'nav-frequencies',
      onClick: () => {
        navigate('/dashboard');
        window.scrollTo({ top: 400, behavior: 'smooth' });
      }
    },
    { 
      icon: BarChart3, 
      labelKey: 'dashboard.stats.title', 
      path: '/history',
      testId: 'nav-stats'
    }
  ];

  // Practitioner feature temporarily hidden for launch
  // if (user.roles?.includes('practitioner') || user.is_practitioner) {
  //   allNavItems.push({
  //     icon: Users,
  //     labelKey: 'nav.practitioner',
  //     path: '/practitioner',
  //     testId: 'nav-practitioner'
  //   });
  // }
  
  // Add admin
  if (user.role === 'admin') {
    allNavItems.push({
      icon: Shield,
      label: 'Admin Panel',
      path: '/admin',
      testId: 'nav-admin',
      isAdmin: true
    });
  }

  const handleNavigate = (item) => {
    if (item.onClick) {
      item.onClick();
    } else {
      navigate(item.path);
    }
    setIsMenuOpen(false);
  };

  const handleLogoutClick = () => {
    setIsMenuOpen(false);
    onLogout();
  };

  const getLabel = (item) => {
    if (item.label) return item.label;
    return t(item.labelKey);
  };

  return (
    <>
      {/* Desktop: Primary links + More button */}
      <div className="header-nav-container">
        <div className="header-nav-primary">
          {allNavItems.filter(item => item.showInHeader).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item)}
                className={`header-nav-btn ${active ? 'active' : ''}`}
                data-testid={`header-${item.testId}`}
              >
                <Icon size={18} />
                <span>{getLabel(item)}</span>
              </button>
            );
          })}
        </div>
        
        {/* Menu Toggle - Shows "Mehr" on desktop, hamburger on mobile */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="menu-toggle-btn"
          data-testid="menu-toggle"
        >
          <Menu size={20} />
          <span className="menu-toggle-text">{t('nav.more')}</span>
        </button>
      </div>

      {/* Slide-out Menu Overlay & Content */}
      {isMenuOpen && (
        <>
          <div className="nav-overlay" onClick={() => setIsMenuOpen(false)} />
          <aside className="nav-slide-menu">
            {/* Menu Header */}
            <div className="slide-menu-header">
              <div className="slide-menu-logo">
                <img
                  src="https://customer-assets.emergentagent.com/job_f48ed865-f137-40ec-8ec5-750617ca0014/artifacts/jm5y5oub_logo.png"
                  alt="ZENTA"
                  className="slide-logo-img"
                />
              </div>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="slide-menu-close"
                data-testid="menu-close"
              >
                <X size={24} />
              </button>
            </div>

            {/* User Info */}
            <div className="slide-menu-user">
              <div className="slide-user-name">{user.name || user.email}</div>
              {subscriptionStatus && (
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate('/subscription/manage');
                  }}
                  className={`slide-subscription-badge ${subscriptionStatus.tier || 'trial'}`}
                  data-testid="sidebar-subscription-badge"
                >
                  <span className="slide-badge-icon">
                    {subscriptionStatus.tier === 'premium' ? '💎' : 
                     subscriptionStatus.tier === 'standard' ? '⭐' : '🆓'}
                  </span>
                  <span className="slide-badge-text">
                    {subscriptionStatus.tier === 'premium' ? 'Premium' :
                     subscriptionStatus.tier === 'standard' ? 'Standard' : t('common.trial')}
                  </span>
                </button>
              )}
              <CreditBadge />
            </div>

            {/* All Navigation Items */}
            <nav className="slide-menu-nav">
              {allNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item)}
                    className={`slide-nav-item ${active ? 'active' : ''} ${item.isAdmin ? 'admin-item' : ''}`}
                    data-testid={item.testId}
                  >
                    <Icon size={20} className="slide-nav-icon" />
                    <span className="slide-nav-label">{getLabel(item)}</span>
                  </button>
                );
              })}
            </nav>

            {/* Logout Button */}
            <div className="slide-menu-footer">
              <button
                onClick={handleLogoutClick}
                className="slide-logout-btn"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
                <span>{t('common.logout')}</span>
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}

export default NavigationMenu;
